// ========================================
// SETTINGS COMPONENT - THRESHOLDS & WEBHOOKS
// ========================================

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import {
    getGlobalThresholds,
    saveGlobalThresholds,
    DEFAULT_THRESHOLDS
} from '../utils/thresholds';
import {
    getWebhookConfig,
    saveWebhookConfig,
    testWebhook,
    clearCooldowns,
    getAlertTypeLabels,
    getAlertCategories
} from '../services/webhookService';
import { logAuditEntry } from '../services/api';
import { onSocketEvent, SOCKET_EVENTS } from '../services/socketService';

const Settings = ({ isOpen, onClose, onSave, allUsers = [], initialTab = 'thresholds', onTabChange }) => {
    const { accounts } = useMsal();
    const { t } = useTranslation();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';

    // Check if current user is admin (from OpenWebUI role)
    const currentUserIsAdmin = (() => {
        const currentUser = allUsers.find(u =>
            u.email?.toLowerCase() === currentUserEmail?.toLowerCase()
        );
        return currentUser?.role === 'admin';
    })();

    // Active tab
    const [activeTab, setActiveTab] = useState('thresholds');

    // Loading state for webhook config
    const [loadingWebhook, setLoadingWebhook] = useState(false);

    // Threshold states (stored as percentages for UI, e.g., 90 for 90%)
    const [budgetAlertPercent, setBudgetAlertPercent] = useState(90);
    const [budgetBlockPercent, setBudgetBlockPercent] = useState(100);
    const [healthWarningPercent, setHealthWarningPercent] = useState(10);
    const [healthCriticalPercent, setHealthCriticalPercent] = useState(30);

    // Track original threshold values to detect changes
    const [originalThresholds, setOriginalThresholds] = useState(null);

    // Webhook states
    const [webhookEnabled, setWebhookEnabled] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookAlerts, setWebhookAlerts] = useState({});
    const [webhookThresholds, setWebhookThresholds] = useState({
        latencyDegradedMs: 2000,
    });
    const [cooldownMinutes, setCooldownMinutes] = useState(15);
    const [webhookType, setWebhookType] = useState('powerautomate'); // 'powerautomate' | 'lambda'
    const [testingWebhook, setTestingWebhook] = useState(false);
    const [testingE2E, setTestingE2E] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [schemaCopied, setSchemaCopied] = useState(false);

    // Track original webhook values
    const [originalWebhook, setOriginalWebhook] = useState(null);

    // Track last known update timestamp for polling
    const [lastKnownUpdate, setLastKnownUpdate] = useState(null);

    // Track if config was updated by another user
    const [externalUpdate, setExternalUpdate] = useState(null);

    // Sync active tab when parent (sidebar) changes the selected sub-tab
    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    useEffect(() => {
        if (!isOpen) return;

        // Load threshold settings (sync - from localStorage)
        const thresholds = getGlobalThresholds();
        setBudgetAlertPercent(Math.round(thresholds.budgetAlert * 100));
        setBudgetBlockPercent(Math.round(thresholds.budgetBlock * 100));
        setHealthWarningPercent(Math.round(thresholds.healthWarning * 100));
        setHealthCriticalPercent(Math.round(thresholds.healthCritical * 100));

        setOriginalThresholds({
            budgetAlert: Math.round(thresholds.budgetAlert * 100),
            budgetBlock: Math.round(thresholds.budgetBlock * 100),
            healthWarning: Math.round(thresholds.healthWarning * 100),
            healthCritical: Math.round(thresholds.healthCritical * 100),
        });

        // Load webhook settings (async - from backend API)
        const loadWebhookConfig = async (forceRefresh = false) => {
            if (!forceRefresh) setLoadingWebhook(true);
            try {
                const webhookConfig = await getWebhookConfig(forceRefresh);
                setWebhookEnabled(webhookConfig.enabled);
                setWebhookUrl(webhookConfig.url || '');
                setWebhookAlerts(webhookConfig.alerts || {});
                setWebhookThresholds(webhookConfig.thresholds || {
                    latencyDegradedMs: 2000,
                });
                setCooldownMinutes(webhookConfig.cooldownMinutes || 15);
                setLastKnownUpdate(webhookConfig.lastUpdatedAt || null);

                if (!forceRefresh) {
                    setOriginalWebhook({
                        enabled: webhookConfig.enabled,
                        url: webhookConfig.url || '',
                        alerts: { ...webhookConfig.alerts },
                        thresholds: { ...webhookConfig.thresholds },
                        cooldownMinutes: webhookConfig.cooldownMinutes || 15,
                    });
                }

                return webhookConfig;
            } catch (error) {
                console.error('Error loading webhook config:', error);
            } finally {
                if (!forceRefresh) setLoadingWebhook(false);
            }
        };

        loadWebhookConfig();

        // Reset test result
        setTestResult(null);
        setExternalUpdate(null);
    }, [isOpen]);

    // Listen for real-time webhook config updates via Socket.IO
    useEffect(() => {
        if (!isOpen) return;

        const handleWebhookUpdate = async (data) => {
            // Only refresh if update was from another user
            if (data.updatedBy !== currentUserEmail) {
                try {
                    const freshConfig = await getWebhookConfig(true); // bypass cache

                    // Config changed by another user - update the form
                    setWebhookEnabled(freshConfig.enabled);
                    setWebhookUrl(freshConfig.url || '');
                    setWebhookAlerts(freshConfig.alerts || {});
                    setWebhookThresholds(freshConfig.thresholds || {
                        latencyDegradedMs: 2000,
                    });
                    setCooldownMinutes(freshConfig.cooldownMinutes || 15);
                    setLastKnownUpdate(freshConfig.lastUpdatedAt);

                    // Update original values so save detection works correctly
                    setOriginalWebhook({
                        enabled: freshConfig.enabled,
                        url: freshConfig.url || '',
                        alerts: { ...freshConfig.alerts },
                        thresholds: { ...freshConfig.thresholds },
                        cooldownMinutes: freshConfig.cooldownMinutes || 15,
                    });

                    // Show notification
                    setExternalUpdate({
                        updatedBy: freshConfig.lastUpdatedBy,
                        updatedAt: freshConfig.lastUpdatedAt
                    });

                    // Auto-hide notification after 5 seconds
                    setTimeout(() => setExternalUpdate(null), 5000);
                } catch (error) {
                    console.error('Error refreshing webhook config:', error);
                }
            }
        };

        const unsubscribe = onSocketEvent(SOCKET_EVENTS.WEBHOOK_UPDATED, handleWebhookUpdate);

        return () => unsubscribe();
    }, [isOpen, currentUserEmail]);

    const handleSave = async () => {
        // Check if thresholds changed
        const thresholdsChanged = originalThresholds && (
            originalThresholds.budgetAlert !== budgetAlertPercent ||
            originalThresholds.budgetBlock !== budgetBlockPercent ||
            originalThresholds.healthWarning !== healthWarningPercent ||
            originalThresholds.healthCritical !== healthCriticalPercent
        );

        // Save threshold settings
        saveGlobalThresholds({
            budgetAlert: budgetAlertPercent / 100,
            budgetBlock: budgetBlockPercent / 100,
            healthWarning: healthWarningPercent / 100,
            healthCritical: healthCriticalPercent / 100,
        });

        // Log audit entry if thresholds changed
        if (thresholdsChanged) {
            await logAuditEntry({
                action: 'UPDATE_GLOBAL_THRESHOLDS',
                userEmail: currentUserEmail,
                details: {
                    previous: originalThresholds,
                    new: {
                        budgetAlert: budgetAlertPercent,
                        budgetBlock: budgetBlockPercent,
                        healthWarning: healthWarningPercent,
                        healthCritical: healthCriticalPercent,
                    },
                    description: `Updated global alert thresholds: Budget Alert ${budgetAlertPercent}%, Budget Block ${budgetBlockPercent}%, Health Warning ${healthWarningPercent}%, Health Critical ${healthCriticalPercent}%`
                }
            });
        }

        // Check if webhook settings changed
        const webhookChanged = originalWebhook && (
            originalWebhook.enabled !== webhookEnabled ||
            originalWebhook.url !== webhookUrl ||
            JSON.stringify(originalWebhook.alerts) !== JSON.stringify(webhookAlerts) ||
            (currentUserIsAdmin && JSON.stringify(originalWebhook.thresholds) !== JSON.stringify(webhookThresholds)) ||
            (currentUserIsAdmin && originalWebhook.cooldownMinutes !== cooldownMinutes)
        );

        // Build webhook config update
        const webhookUpdate = {
            enabled: webhookEnabled,
            url: webhookUrl,
            alerts: webhookAlerts,
        };

        // Only admins can update thresholds and cooldown
        if (currentUserIsAdmin) {
            webhookUpdate.thresholds = webhookThresholds;
            webhookUpdate.cooldownMinutes = cooldownMinutes;
        }

        // Save webhook settings to backend
        await saveWebhookConfig(webhookUpdate);

        // Log audit entry if webhook changed
        if (webhookChanged) {
            await logAuditEntry({
                action: 'UPDATE_WEBHOOK_CONFIG',
                userEmail: currentUserEmail,
                details: {
                    enabled: webhookEnabled,
                    urlConfigured: !!webhookUrl,
                    enabledAlerts: Object.entries(webhookAlerts)
                        .filter(([, enabled]) => enabled)
                        .map(([key]) => key),
                    description: webhookEnabled
                        ? `Webhook ${originalWebhook.enabled ? 'updated' : 'enabled'}`
                        : 'Webhook disabled'
                }
            });
        }

        // Notify parent
        onSave({});
        onClose();
    };

    const handleResetThresholds = () => {
        setBudgetAlertPercent(Math.round(DEFAULT_THRESHOLDS.BUDGET_ALERT * 100));
        setBudgetBlockPercent(Math.round(DEFAULT_THRESHOLDS.BUDGET_BLOCK * 100));
        setHealthWarningPercent(Math.round(DEFAULT_THRESHOLDS.HEALTH_WARNING * 100));
        setHealthCriticalPercent(Math.round(DEFAULT_THRESHOLDS.HEALTH_CRITICAL * 100));
    };

    const handleTestWebhook = async () => {
        setTestingWebhook(true);
        setTestResult(null);

        // Save the URL temporarily before testing
        await saveWebhookConfig({
            url: webhookUrl,
            enabled: true,
        });

        const result = await testWebhook();
        setTestResult(result);
        setTestingWebhook(false);

        // Restore original enabled state
        await saveWebhookConfig({
            url: webhookUrl,
            enabled: webhookEnabled,
        });
    };

    const handleTestE2E = async () => {
        setTestingE2E(true);
        setTestResult(null);

        // Save URL temporarily so the proxy has it
        await saveWebhookConfig({ url: webhookUrl, enabled: true });

        try {
            const { authenticatedFetch } = await import('../services/authService');
            const config = (await import('../config')).default;
            const response = await authenticatedFetch(`${config.backendApiUrl}/webhook-config/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    timestamp: new Date().toISOString(),
                    source: 'Ask Operations Dashboard',
                    alertType: 'budgetWarning',
                    severity: 'warning',
                    title: '[TEST] Team Budget Warning',
                    message: '[TEST] CNX Users has reached 92% of their monthly budget.',
                    details: {
                        teamName: 'CNX Users',
                        usagePercent: 92,
                        spend: 92.10,
                        budget: 100.00,
                        remaining: 7.90,
                        blocked: false,
                        test: true,
                    }
                })
            });
            const data = await response.json();
            setTestResult(data.success
                ? { success: true, message: 'Test budget alert sent — check Teams!' }
                : { success: false, reason: data.error || `HTTP ${response.status}` }
            );
        } catch (err) {
            setTestResult({ success: false, reason: err.message });
        }

        // Restore original enabled state
        await saveWebhookConfig({ url: webhookUrl, enabled: webhookEnabled });
        setTestingE2E(false);
    };

    const handleClearCooldowns = async () => {
        const success = await clearCooldowns();
        if (success) {
            setTestResult({ success: true, message: 'Alert timers reset' });
        } else {
            setTestResult({ success: false, reason: 'Failed to reset timers' });
        }
        setTimeout(() => setTestResult(null), 2000);
    };

    // JSON Schema for Power Automate
    const webhookSchema = {
        type: "object",
        properties: {
            timestamp: { type: "string" },
            source: { type: "string" },
            alertType: { type: "string" },
            severity: { type: "string", enum: ["info", "warning", "critical"] },
            title: { type: "string" },
            message: { type: "string" },
            details: {
                type: "object",
                properties: {
                    teamId: { type: "string" },
                    teamName: { type: "string" },
                    spend: { type: "number" },
                    budget: { type: "number" },
                    usagePercent: { type: "number" },
                    remaining: { type: "number" },
                    blocked: { type: "boolean" },
                    service: { type: "string" },
                    status: { type: "string" },
                    latency: { type: "number" },
                    total: { type: "number" },
                    unhealthy: { type: "number" },
                    unhealthyPercent: { type: "number" },
                    unhealthyModels: { type: "array", items: { type: "string" } },
                    userName: { type: "string" },
                    userEmail: { type: "string" },
                    disabledBy: { type: "string" },
                    enabledBy: { type: "string" },
                    addedBy: { type: "string" },
                    removedBy: { type: "string" },
                    reason: { type: "string" },
                    unblockedBy: { type: "string" }
                }
            }
        },
        required: ["timestamp", "source", "alertType", "severity", "title", "message"]
    };

    const handleCopySchema = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(webhookSchema, null, 2));
            setSchemaCopied(true);
            setTimeout(() => setSchemaCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy schema:', err);
        }
    };

    const handleAlertToggle = (alertKey) => {
        setWebhookAlerts(prev => ({
            ...prev,
            [alertKey]: !prev[alertKey]
        }));
    };

    const handleSelectAllInCategory = (categoryAlerts, selectAll) => {
        const updates = {};
        categoryAlerts.forEach(key => {
            updates[key] = selectAll;
        });
        setWebhookAlerts(prev => ({
            ...prev,
            ...updates
        }));
    };

    if (!isOpen) return null;

    const alertLabels = getAlertTypeLabels();
    const alertCategories = getAlertCategories();

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'var(--bg)',
            overflow: 'hidden',
        }}>
            {/* Panel header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'clamp(12px, 2vw, 16px) clamp(16px, 3vw, 24px)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface)',
                flexShrink: 0,
            }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--fg-primary)' }}>
                    {t('settings.title')}
                </h2>
                <button className="btn btn-secondary" onClick={onClose} style={{ padding: '6px 14px', fontSize: '0.82rem' }}>
                    Back to Dashboard
                </button>
            </div>

                <div style={{ overflowY: 'auto', flex: 1, padding: 'clamp(16px, 3vw, 28px) clamp(16px, 3vw, 24px)' }}>

                    {/* Thresholds Tab */}
                    {activeTab === 'thresholds' && (
                        <>
                            <section style={{ marginBottom: '2rem' }}>
                                <h3 style={{ color: 'var(--fg-primary)', marginTop: 0, marginBottom: '0.75rem', fontSize: '1.25rem' }}>
                                    {t('settings.thresholds')}
                                </h3>
                                <p style={{ color: 'var(--fg-secondary)', lineHeight: '1.6', marginBottom: '1rem' }}>
                                    {t('settings.thresholdDescription')}
                                </p>

                                {/* Budget Thresholds */}
                                <div style={{
                                    backgroundColor: 'var(--border)',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    border: '1px solid var(--border-hover)'
                                }}>
                                    <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--fg-primary)', fontSize: '1rem' }}>
                                        {t('settings.budgetThresholds')}
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                                        <div>
                                            <label title={t('settings.tooltipBudgetAlert')} style={{
                                                display: 'block',
                                                marginBottom: '0.5rem',
                                                fontWeight: '500',
                                                color: 'var(--fg-primary)',
                                                fontSize: '0.9rem',
                                                cursor: 'help'
                                            }}>
                                                {t('settings.alertThreshold')}
                                                <span style={{ color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                                    {t('settings.showWarning')}
                                                </span>
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={budgetAlertPercent}
                                                    onChange={(e) => setBudgetAlertPercent(Math.max(1, Math.min(100, parseInt(e.target.value) || 90)))}
                                                    style={{
                                                        width: '80px',
                                                        padding: '0.5rem',
                                                        border: '1px solid var(--border-hover)',
                                                        borderBottom: '3px solid var(--warning)',
                                                        borderRadius: '4px',
                                                        fontSize: '0.95rem',
                                                        textAlign: 'center',
                                                        backgroundColor: 'var(--surface)',
                                                        color: 'var(--fg-primary)',
                                                    }}
                                                />
                                                <span style={{ color: 'var(--fg-secondary)' }}>%</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label title={t('settings.tooltipBudgetBlock')} style={{
                                                display: 'block',
                                                marginBottom: '0.5rem',
                                                fontWeight: '500',
                                                color: 'var(--fg-primary)',
                                                fontSize: '0.9rem',
                                                cursor: 'help'
                                            }}>
                                                {t('settings.blockThreshold')}
                                                <span style={{ color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                                    {t('settings.autoBlock')}
                                                </span>
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="200"
                                                    value={budgetBlockPercent}
                                                    onChange={(e) => setBudgetBlockPercent(Math.max(1, Math.min(200, parseInt(e.target.value) || 100)))}
                                                    style={{
                                                        width: '80px',
                                                        padding: '0.5rem',
                                                        border: '1px solid var(--border-hover)',
                                                        borderBottom: '3px solid var(--critical)',
                                                        borderRadius: '4px',
                                                        fontSize: '0.95rem',
                                                        textAlign: 'center',
                                                        backgroundColor: 'var(--surface)',
                                                        color: 'var(--fg-primary)',
                                                    }}
                                                />
                                                <span style={{ color: 'var(--fg-secondary)' }}>%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Health Thresholds */}
                                <div style={{
                                    backgroundColor: 'var(--border)',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    border: '1px solid var(--border-hover)'
                                }}>
                                    <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--fg-primary)', fontSize: '1rem' }}>
                                        {t('settings.healthThresholds')}
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                                        <div>
                                            <label title={t('settings.tooltipHealthWarning')} style={{
                                                display: 'block',
                                                marginBottom: '0.5rem',
                                                fontWeight: '500',
                                                color: 'var(--fg-primary)',
                                                fontSize: '0.9rem',
                                                cursor: 'help'
                                            }}>
                                                {t('settings.warningThreshold')}
                                                <span style={{ color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                                    {t('settings.yellowStatus')}
                                                </span>
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={healthWarningPercent}
                                                    onChange={(e) => setHealthWarningPercent(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                                                    style={{
                                                        width: '80px',
                                                        padding: '0.5rem',
                                                        border: '1px solid var(--border-hover)',
                                                        borderBottom: '3px solid var(--warning)',
                                                        borderRadius: '4px',
                                                        fontSize: '0.95rem',
                                                        textAlign: 'center',
                                                        backgroundColor: 'var(--surface)',
                                                        color: 'var(--fg-primary)',
                                                    }}
                                                />
                                                <span style={{ color: 'var(--fg-secondary)' }}>%</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label title={t('settings.tooltipHealthCritical')} style={{
                                                display: 'block',
                                                marginBottom: '0.5rem',
                                                fontWeight: '500',
                                                color: 'var(--fg-primary)',
                                                fontSize: '0.9rem',
                                                cursor: 'help'
                                            }}>
                                                {t('settings.criticalThreshold')}
                                                <span style={{ color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                                    {t('settings.redStatus')}
                                                </span>
                                            </label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={healthCriticalPercent}
                                                    onChange={(e) => setHealthCriticalPercent(Math.max(1, Math.min(100, parseInt(e.target.value) || 30)))}
                                                    style={{
                                                        width: '80px',
                                                        padding: '0.5rem',
                                                        border: '1px solid var(--border-hover)',
                                                        borderBottom: '3px solid var(--critical)',
                                                        borderRadius: '4px',
                                                        fontSize: '0.95rem',
                                                        textAlign: 'center',
                                                        backgroundColor: 'var(--surface)',
                                                        color: 'var(--fg-primary)',
                                                    }}
                                                />
                                                <span style={{ color: 'var(--fg-secondary)' }}>%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={handleResetThresholds}
                                    className="btn btn-secondary"
                                >
                                    {t('settings.resetDefaults')}
                                </button>
                            </section>

                            <section style={{ marginBottom: 0 }}>
                                <h3 style={{ color: 'var(--fg-primary)', marginTop: 0, marginBottom: '0.75rem', fontSize: '1.25rem' }}>
                                    {t('settings.thresholdBehavior')}
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                                    <div style={{ backgroundColor: 'var(--border)', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid var(--border-hover)' }}>
                                        <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--fg-primary)', fontSize: '0.9rem', fontWeight: '600' }}>
                                            {t('settings.thresholdBehaviorBudgetTitle')}
                                        </h4>
                                        <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
                                            <li style={{
                                                padding: '0.4rem 0.6rem',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--warning-bg)',
                                                marginBottom: '0.4rem',
                                                borderLeft: '4px solid var(--warning)',
                                                fontSize: '0.85rem',
                                                color: 'var(--fg-primary)',
                                                lineHeight: '1.5'
                                            }}>
                                                <strong>{t('settings.alertThreshold')}:</strong> {t('settings.budgetAlertBehavior')}
                                            </li>
                                            <li style={{
                                                padding: '0.4rem 0.6rem',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--critical-bg)',
                                                borderLeft: '4px solid var(--critical)',
                                                fontSize: '0.85rem',
                                                color: 'var(--fg-primary)',
                                                lineHeight: '1.5'
                                            }}>
                                                <strong>{t('settings.blockThreshold')}:</strong> {t('settings.budgetBlockBehavior')}
                                            </li>
                                        </ul>
                                    </div>
                                    <div style={{ backgroundColor: 'var(--border)', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid var(--border-hover)' }}>
                                        <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--fg-primary)', fontSize: '0.9rem', fontWeight: '600' }}>
                                            {t('settings.thresholdBehaviorHealthTitle')}
                                        </h4>
                                        <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
                                            <li style={{
                                                padding: '0.4rem 0.6rem',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--warning-bg)',
                                                marginBottom: '0.4rem',
                                                borderLeft: '4px solid var(--warning)',
                                                fontSize: '0.85rem',
                                                color: 'var(--fg-primary)',
                                                lineHeight: '1.5'
                                            }}>
                                                <strong>{t('settings.warningThreshold')}:</strong> {t('settings.healthWarningBehavior')}
                                            </li>
                                            <li style={{
                                                padding: '0.4rem 0.6rem',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--critical-bg)',
                                                borderLeft: '4px solid var(--critical)',
                                                fontSize: '0.85rem',
                                                color: 'var(--fg-primary)',
                                                lineHeight: '1.5'
                                            }}>
                                                <strong>{t('settings.criticalThreshold')}:</strong> {t('settings.healthCriticalBehavior')}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </>
                    )}

                    {/* Webhook Tab */}
                    {activeTab === 'webhook' && (
                        <>
                            {/* External update notification */}
                            {externalUpdate && (
                                <div style={{
                                    backgroundColor: 'var(--warning-bg)',
                                    border: '1px solid var(--warning)',
                                    borderRadius: '8px',
                                    padding: '0.75rem 1rem',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{ color: 'var(--warning)', fontSize: '0.9rem' }}>
                                        Settings were updated by <strong>{externalUpdate.updatedBy}</strong>. The form has been refreshed with the latest values.
                                    </span>
                                </div>
                            )}
                            {loadingWebhook ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
                                    {t('settings.loadingWebhook')}
                                </div>
                            ) : (
                                <>
                                    <section style={{ marginBottom: '2rem' }}>
                                        <h3 style={{ color: 'var(--fg-primary)', marginTop: 0, marginBottom: '0.75rem', fontSize: '1.25rem' }}>
                                            {t('settings.webhookConfiguration')}
                                        </h3>
                                        <p style={{ color: 'var(--fg-secondary)', lineHeight: '1.6', marginBottom: '1rem' }}>
                                            {t('settings.webhookConfigDescription')}
                                        </p>

                                        {/* Enable/Disable */}
                                        <div style={{
                                            backgroundColor: webhookEnabled ? 'rgba(74,222,128,0.08)' : 'var(--border)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            marginBottom: '1rem',
                                            border: `1px solid ${webhookEnabled ? 'var(--success)' : 'var(--border-hover)'}`
                                        }}>
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                userSelect: 'none'
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={webhookEnabled}
                                                    onChange={(e) => setWebhookEnabled(e.target.checked)}
                                                    style={{ marginRight: '0.75rem', width: '18px', height: '18px' }}
                                                />
                                                <span style={{
                                                    fontWeight: '600',
                                                    color: webhookEnabled ? 'var(--success)' : 'var(--fg-primary)',
                                                    fontSize: '1rem'
                                                }}>
                                                    {t('settings.enableWebhookNotifications')}
                                                </span>
                                            </label>
                                        </div>

                                        {/* Webhook Type Selector */}
                                        <div style={{
                                            backgroundColor: 'var(--border)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            marginBottom: '1rem',
                                            border: '1px solid var(--border-hover)',
                                            opacity: webhookEnabled ? 1 : 0.6
                                        }}>
                                            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500', color: 'var(--fg-primary)', fontSize: '0.9rem' }}>
                                                Alert Destination
                                            </label>
                                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                                {[
                                                    { id: 'powerautomate', label: 'Microsoft Power Automate', icon: '' },
                                                    { id: 'lambda', label: 'AWS Lambda', icon: '' },
                                                ].map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        onClick={() => setWebhookType(opt.id)}
                                                        disabled={!webhookEnabled}
                                                        style={{
                                                            flex: 1,
                                                            padding: '0.75rem',
                                                            borderRadius: '8px',
                                                            border: webhookType === opt.id ? '2px solid var(--signal-blue)' : '2px solid var(--border-hover)',
                                                            backgroundColor: webhookType === opt.id ? 'var(--info-bg)' : 'var(--surface)',
                                                            color: webhookType === opt.id ? 'var(--signal-blue)' : 'var(--fg-secondary)',
                                                            fontWeight: webhookType === opt.id ? '600' : '400',
                                                            cursor: webhookEnabled ? 'pointer' : 'not-allowed',
                                                            fontSize: '0.88rem',
                                                            transition: 'all 0.15s',
                                                            fontFamily: 'var(--font-sans)',
                                                        }}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Contextual help per type */}
                                            {webhookType === 'powerautomate' && (
                                                <div style={{ marginTop: '0.75rem', padding: '0.65rem 0.85rem', backgroundColor: 'var(--info-bg)', borderRadius: '6px', border: '1px solid var(--signal-blue)', fontSize: '0.82rem', color: 'var(--signal-blue)', lineHeight: '1.6' }}>
                                                    <strong>Setup:</strong> In Power Automate, create a flow with the <em>"When an HTTP request is received"</em> trigger.
                                                    Copy the generated URL and paste it below. Use <strong>Copy Schema</strong> to import the payload structure,
                                                    then add a Condition on <code>triggerBody()?['alertType']</code> to route alerts.
                                                </div>
                                            )}
                                            {webhookType === 'lambda' && (
                                                <div style={{ marginTop: '0.75rem', padding: '0.65rem 0.85rem', backgroundColor: 'var(--warning-bg)', borderRadius: '6px', border: '1px solid var(--warning)', fontSize: '0.82rem', color: 'var(--warning)', lineHeight: '1.6' }}>
                                                    <strong>Setup:</strong> Create an <strong>AWS Lambda</strong> function and expose it via <strong>API Gateway</strong> (HTTP API, POST method).
                                                    Paste the API Gateway invoke URL below. Your Lambda receives the JSON payload and can forward to SES (email),
                                                    SNS (SMS/push), Slack, or any other AWS service. Use <strong>Copy Schema</strong> to see the full payload structure.
                                                    <br /><br />
                                                    <strong>Minimal Lambda handler (Node.js):</strong>
                                                    <pre style={{ margin: '0.5rem 0 0 0', padding: '0.5rem', backgroundColor: 'var(--bg)', color: 'var(--fg-secondary)', borderRadius: '4px', fontSize: '0.78rem', overflowX: 'auto' }}>{`export const handler = async (event) => {
  const alert = JSON.parse(event.body);
  if (alert.alertType === 'budgetWarning') {
    // e.g. send via SES, SNS, Slack...
  }
  return { statusCode: 200, body: 'OK' };
};`}</pre>
                                                </div>
                                            )}
                                        </div>

                                        {/* Webhook URL */}
                                        <div style={{
                                            backgroundColor: 'var(--border)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            marginBottom: '1rem',
                                            border: '1px solid var(--border-hover)',
                                            opacity: webhookEnabled ? 1 : 0.6
                                        }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '0.5rem',
                                                fontWeight: '500',
                                                color: 'var(--fg-primary)',
                                                fontSize: '0.9rem'
                                            }}>
                                                {webhookType === 'lambda' ? 'API Gateway URL' : t('settings.webhookUrl')}
                                            </label>
                                            <input
                                                type="url"
                                                value={webhookUrl}
                                                onChange={(e) => setWebhookUrl(e.target.value)}
                                                placeholder={webhookType === 'lambda'
                                                    ? 'https://xxxxxxxxxx.execute-api.ap-southeast-1.amazonaws.com/prod/alerts'
                                                    : t('settings.webhookUrlPlaceholder')}
                                                disabled={!webhookEnabled}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '1px solid var(--border-hover)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.9rem',
                                                    boxSizing: 'border-box',
                                                    backgroundColor: 'var(--surface)',
                                                    color: 'var(--fg-primary)',
                                                    fontFamily: 'var(--font-sans)',
                                                }}
                                            />
                                            <p style={{ fontSize: '0.85rem', color: 'var(--muted)', margin: '0.5rem 0 0 0' }}>
                                                {webhookType === 'lambda'
                                                    ? 'Your AWS API Gateway invoke URL (HTTP API, POST method, no auth header required if using API key in URL)'
                                                    : t('settings.webhookUrlDescription')}
                                            </p>

                                            {/* Test Button & Copy Schema */}
                                            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                                <button
                                                    onClick={handleTestWebhook}
                                                    disabled={!webhookUrl || testingWebhook || testingE2E}
                                                    className="btn btn-secondary"
                                                    title="Sends a generic test ping to verify the URL is reachable"
                                                >
                                                    {testingWebhook ? t('settings.testing') : t('settings.sendTest')}
                                                </button>
                                                <button
                                                    onClick={handleTestE2E}
                                                    disabled={!webhookUrl || testingWebhook || testingE2E}
                                                    className="btn btn-secondary"
                                                    title="Sends a realistic budgetWarning payload to test your full Power Automate flow"
                                                >
                                                    {testingE2E ? 'Sending...' : 'Test Budget Alert'}
                                                </button>
                                                <button
                                                    onClick={handleCopySchema}
                                                    className="btn btn-secondary"
                                                    title="Copy JSON Schema for Power Automate"
                                                    style={{ color: schemaCopied ? 'var(--success)' : undefined }}
                                                >
                                                    {schemaCopied ? t('settings.copied') : t('settings.copySchema')}
                                                </button>
                                                {testResult && (
                                                    <span style={{
                                                        fontSize: '0.9rem',
                                                        color: testResult.success ? 'var(--success)' : 'var(--critical)'
                                                    }}>
                                                        {testResult.success
                                                            ? (testResult.message || t('settings.testSuccess'))
                                                            : `${t('common.failed')}: ${testResult.reason}`}
                                                    </span>
                                                )}
                                            </div>
                                            <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                                                {t('settings.schemaNote')}
                                            </p>
                                        </div>

                                        {/* Cooldown - Admin only editable */}
                                        <div style={{
                                            backgroundColor: 'var(--border)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            marginBottom: '1rem',
                                            border: '1px solid var(--border-hover)',
                                            opacity: webhookEnabled ? 1 : 0.6
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div>
                                                    <label style={{
                                                        display: 'block',
                                                        fontWeight: '500',
                                                        color: 'var(--fg-primary)',
                                                        fontSize: '0.9rem'
                                                    }}>
                                                        {t('settings.alertCooldown')}
                                                        {!currentUserIsAdmin && (
                                                            <span style={{ color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                                                                {t('settings.adminOnly')}
                                                            </span>
                                                        )}
                                                    </label>
                                                    <p style={{ fontSize: '0.85rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>
                                                        {t('settings.cooldownDescription')}
                                                    </p>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <input
                                                        type="number"
                                                        min="5"
                                                        max="60"
                                                        value={cooldownMinutes}
                                                        onChange={(e) => setCooldownMinutes(Math.max(5, Math.min(60, parseInt(e.target.value) || 15)))}
                                                        disabled={!webhookEnabled || !currentUserIsAdmin}
                                                        style={{
                                                            width: '70px',
                                                            padding: '0.5rem',
                                                            border: '1px solid var(--border-hover)',
                                                            borderRadius: '4px',
                                                            fontSize: '0.95rem',
                                                            textAlign: 'center',
                                                            backgroundColor: currentUserIsAdmin ? 'var(--surface)' : 'var(--border)',
                                                            color: 'var(--fg-primary)',
                                                        }}
                                                    />
                                                    <span style={{ color: 'var(--fg-secondary)' }}>{t('settings.minutes')}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleClearCooldowns}
                                                disabled={!webhookEnabled}
                                                className="btn btn-secondary"
                                                style={{ marginTop: '0.75rem' }}
                                                title={t('settings.resetAlertTimers')}
                                            >
                                                {t('settings.resetAlertTimers')}
                                            </button>
                                        </div>
                                    </section>

                                    <section style={{ marginBottom: '1rem' }}>
                                        <h3 style={{ color: 'var(--fg-primary)', marginTop: 0, marginBottom: '0.75rem', fontSize: '1.25rem' }}>
                                            {t('settings.alertTypes')}
                                        </h3>
                                        <p style={{ color: 'var(--fg-secondary)', lineHeight: '1.6', marginBottom: '1rem' }}>
                                            {t('settings.alertTypesDescription')}
                                        </p>

                                        {Object.entries(alertCategories).map(([category, alertKeys]) => {
                                            const allSelected = alertKeys.every(key => webhookAlerts[key]);

                                            return (
                                                <div
                                                    key={category}
                                                    style={{
                                                        backgroundColor: 'var(--border)',
                                                        padding: '1rem',
                                                        borderRadius: '8px',
                                                        marginBottom: '0.75rem',
                                                        border: '1px solid var(--border-hover)',
                                                        opacity: webhookEnabled ? 1 : 0.6
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: '0.75rem'
                                                    }}>
                                                        <h4 style={{ margin: 0, color: 'var(--fg-primary)', fontSize: '0.95rem' }}>
                                                            {category}
                                                        </h4>
                                                        <button
                                                            onClick={() => handleSelectAllInCategory(alertKeys, !allSelected)}
                                                            disabled={!webhookEnabled}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                                        >
                                                            {allSelected ? t('settings.deselectAll') : t('settings.selectAll')}
                                                        </button>
                                                    </div>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                                        gap: '0.5rem'
                                                    }}>
                                                        {alertKeys.map(key => (
                                                            <label
                                                                key={key}
                                                                title={t(`settings.alertTooltip_${key}`)}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    cursor: webhookEnabled ? 'pointer' : 'not-allowed',
                                                                    padding: '0.5rem',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: webhookAlerts[key] ? 'var(--info-bg)' : 'transparent',
                                                                    border: `1px solid ${webhookAlerts[key] ? 'var(--signal-blue)' : 'transparent'}`
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={webhookAlerts[key] || false}
                                                                    onChange={() => handleAlertToggle(key)}
                                                                    disabled={!webhookEnabled}
                                                                    style={{ marginRight: '0.5rem' }}
                                                                />
                                                                <span style={{
                                                                    fontSize: '0.85rem',
                                                                    color: webhookAlerts[key] ? 'var(--signal-blue)' : 'var(--fg-secondary)'
                                                                }}>
                                                                    {alertLabels[key]}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </section>

                                    <section style={{ marginBottom: 0 }}>
                                        <h3 style={{ color: 'var(--fg-primary)', marginTop: 0, marginBottom: '0.75rem', fontSize: '1.25rem' }}>
                                            {t('settings.webhookTriggerThresholds')}
                                            {!currentUserIsAdmin && (
                                                <span style={{ color: 'var(--muted)', fontWeight: 'normal', marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                                                    {t('settings.adminOnly')}
                                                </span>
                                            )}
                                        </h3>
                                        <div style={{
                                            backgroundColor: 'var(--border)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-hover)',
                                            opacity: webhookEnabled ? 1 : 0.6
                                        }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
                                                <div>
                                                    <label title={t('settings.tooltipWebhookLatency')} style={{
                                                        display: 'block',
                                                        marginBottom: '0.5rem',
                                                        fontWeight: '500',
                                                        color: 'var(--fg-primary)',
                                                        fontSize: '0.85rem',
                                                        cursor: 'help'
                                                    }}>
                                                        {t('settings.latencyDegraded')}
                                                    </label>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                        <input
                                                            type="number"
                                                            min="500"
                                                            max="10000"
                                                            step="100"
                                                            value={webhookThresholds.latencyDegradedMs}
                                                            onChange={(e) => setWebhookThresholds(prev => ({
                                                                ...prev,
                                                                latencyDegradedMs: Math.max(500, Math.min(10000, parseInt(e.target.value) || 2000))
                                                            }))}
                                                            disabled={!webhookEnabled || !currentUserIsAdmin}
                                                            style={{
                                                                width: '70px',
                                                                padding: '0.4rem',
                                                                border: '1px solid var(--border-hover)',
                                                                borderRadius: '4px',
                                                                fontSize: '0.9rem',
                                                                textAlign: 'center',
                                                                backgroundColor: currentUserIsAdmin ? 'var(--surface)' : 'var(--border)',
                                                                color: 'var(--fg-primary)',
                                                            }}
                                                        />
                                                        <span style={{ color: 'var(--fg-secondary)', fontSize: '0.85rem' }}>{t('settings.ms')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </>
                            )}
                        </>
                    )}
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 10,
                    padding: 'clamp(10px, 2vw, 14px) clamp(16px, 3vw, 24px)',
                    borderTop: '1px solid var(--border)',
                    background: 'var(--surface)',
                    flexShrink: 0,
                }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        {t('settings.cancel')}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave}>
                        {t('settings.save')}
                    </button>
                </div>
        </div>
    );
};

export default Settings;

